import { useState, useEffect } from "react";
import {
  deliverability,
  type DeliverabilityResponse,
  type WarmupPlan,
  type BlacklistCheckSummary,
  type BlacklistResult,
  type WarmupPoolStats,
  type WarmupInbox,
  type WarmupInboxInput,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ShieldCheck,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Flame,
  Plus,
  Trash2,
  X,
  RefreshCw,
  ExternalLink,
  Users,
  CheckCircle,
  MessageCircleWarning,
  Mail,
  Power,
  Loader2,
  SendHorizonal,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ── Warming ramp curve (days → daily limit) ───────────────────────────────────
// Linear interpolation between defined control points.
const RAMP: [number, number][] = [
  [1, 20], [2, 40], [3, 60], [4, 100], [7, 200],
  [10, 400], [14, 700], [21, 1200], [30, 2000],
];

function getDailyLimit(day: number): number {
  if (day <= RAMP[0][0]) return RAMP[0][1];
  if (day >= RAMP[RAMP.length - 1][0]) return RAMP[RAMP.length - 1][1];
  for (let i = 0; i < RAMP.length - 1; i++) {
    const [d0, l0] = RAMP[i];
    const [d1, l1] = RAMP[i + 1];
    if (day >= d0 && day <= d1) {
      const t = (day - d0) / (d1 - d0);
      return Math.round(l0 + t * (l1 - l0));
    }
  }
  return 2000;
}

// ── Reputation color helper ───────────────────────────────────────────────────
function reputationColor(score: number) {
  if (score >= 80) return { text: "text-green-600", bg: "bg-green-500", label: "Excellent" };
  if (score >= 60) return { text: "text-amber-600", bg: "bg-amber-500", label: "Fair" };
  return { text: "text-red-600", bg: "bg-red-500", label: "At Risk" };
}

// ── Metric card ───────────────────────────────────────────────────────────────
function MetricCard({
  label, value, sub, icon: Icon, trend, good,
}: {
  label: string; value: string; sub?: string;
  icon: React.ElementType; trend?: "up" | "down" | null; good?: "up" | "down";
}) {
  const trendGood = trend && good ? trend === good : null;
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-xl bg-black/[0.03]">
          <Icon size={18} className="text-slate" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-semibold ${
            trendGood ? "text-green-600" : "text-red-500"
          }`}>
            {trend === "up" ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            {trend === "up" ? "Up" : "Down"}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-ink">{value}</p>
      <p className="text-xs font-semibold text-slate mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate/70 mt-1">{sub}</p>}
    </div>
  );
}

// ── Warmup plan card ──────────────────────────────────────────────────────────
function WarmupCard({
  plan, onDelete, isDeleting, onReset, isResetting,
}: {
  plan: WarmupPlan; onDelete: () => void; isDeleting: boolean;
  onReset: () => void; isResetting: boolean;
}) {
  const limit = getDailyLimit(plan.day_number);
  const progress = plan.daily_limit > 0
    ? Math.min(100, Math.round((plan.emails_sent_today / plan.daily_limit) * 100))
    : 0;

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-coral" />
            <p className="font-semibold text-ink">{plan.domain}</p>
            <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-bold">
              ACTIVE
            </span>
          </div>
          <p className="text-xs text-slate mt-1">Day {plan.day_number} · Target: {limit} emails/day</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onReset} disabled={isResetting} title="Reset warmup to day 1"
            className="p-1.5 rounded-lg text-slate hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-40">
            <RefreshCw size={14} className={isResetting ? "animate-spin" : ""} />
          </button>
          <button onClick={onDelete} disabled={isDeleting}
            className="p-1.5 rounded-lg text-slate hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Today's progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate mb-1.5">
          <span>Today's sends</span>
          <span className="font-semibold">{plan.emails_sent_today} / {plan.daily_limit}</span>
        </div>
        <div className="h-2 bg-black/[0.05] rounded-full overflow-hidden">
          <div
            className="h-full bg-coral rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Ramp preview */}
      <div className="grid grid-cols-4 gap-1.5 text-center">
        {[7, 14, 21, 30].map((d) => (
          <div key={d} className={`p-2 rounded-lg text-xs ${
            plan.day_number >= d ? "bg-coral/10 text-coral font-semibold" : "bg-black/[0.03] text-slate"
          }`}>
            <p className="font-bold">{getDailyLimit(d).toLocaleString()}</p>
            <p className="text-[10px] mt-0.5">Day {d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── IP Health Card ────────────────────────────────────────────────────────────
function IpHealthCard({
  initial,
}: {
  initial: BlacklistCheckSummary | null;
}) {
  const [data, setData]       = useState<BlacklistCheckSummary | null>(initial);
  const [checking, setChecking] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Sync if parent reloads
  useEffect(() => { setData(initial); }, [initial]);

  const handleCheck = async () => {
    setChecking(true);
    setError(null);
    try {
      const res = await deliverability.checkBlacklist();
      setData(res.blacklist);
    } catch (e: any) {
      setError(e.message || "Check failed — make sure your SMTP account is configured.");
    } finally {
      setChecking(false);
    }
  };

  const WEIGHT_ORDER = { critical: 0, high: 1, medium: 2 };
  const sorted = data
    ? [...data.results].sort(
        (a, b) =>
          (b.listed ? 1 : 0) - (a.listed ? 1 : 0) ||
          WEIGHT_ORDER[a.weight] - WEIGHT_ORDER[b.weight]
      )
    : [];

  const weightColor = (w: BlacklistResult["weight"], listed: boolean) => {
    if (!listed) return "text-green-600";
    return w === "critical" ? "text-red-600" : w === "high" ? "text-amber-600" : "text-amber-500";
  };

  const weightBg = (w: BlacklistResult["weight"], listed: boolean) => {
    if (!listed) return "bg-green-50";
    return w === "critical" ? "bg-red-50" : "bg-amber-50";
  };

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          {data?.anyListed
            ? <ShieldAlert size={18} className="text-red-500" />
            : <ShieldCheck size={18} className="text-green-500" />
          }
          <div>
            <p className="font-semibold text-ink">IP Health</p>
            {data && (
              <p className="text-xs text-slate mt-0.5">
                {data.ip} · checked{" "}
                {new Date(data.checkedAt).toLocaleDateString("en-US", {
                  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleCheck}
          disabled={checking}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-black/[0.08] text-xs font-semibold text-slate hover:border-black/20 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={12} className={checking ? "animate-spin" : ""} />
          {checking ? "Checking…" : "Check Now"}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Not yet checked */}
      {!data && !checking && !error && (
        <div className="py-8 text-center text-slate">
          <ShieldCheck size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm font-medium">Not checked yet</p>
          <p className="text-xs mt-1 mb-4">
            Click <span className="font-semibold">Check Now</span> to scan your sending IP against 10 blacklists.
          </p>
        </div>
      )}

      {/* Summary badge */}
      {data && (
        <div className={`flex items-center gap-2 p-3 rounded-xl mb-4 ${
          data.anyListed ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"
        }`}>
          {data.anyListed ? (
            <>
              <ShieldAlert size={15} className="text-red-500 shrink-0" />
              <span className="text-sm font-semibold text-red-700">
                Listed on {data.listedOn.length} blacklist{data.listedOn.length !== 1 ? "s" : ""}:{" "}
                {data.listedOn.join(", ")}
              </span>
            </>
          ) : (
            <>
              <ShieldCheck size={15} className="text-green-600 shrink-0" />
              <span className="text-sm font-semibold text-green-700">
                Clean — not listed on any of the 10 blacklists checked
              </span>
            </>
          )}
        </div>
      )}

      {/* Per-list results */}
      {sorted.length > 0 && (
        <div className="space-y-1.5">
          {sorted.map((r) => (
            <div
              key={r.domain}
              className={`flex items-center justify-between px-3 py-2 rounded-lg ${weightBg(r.weight, r.listed)}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-xs font-bold ${weightColor(r.weight, r.listed)}`}>
                  {r.listed ? "✗" : "✓"}
                </span>
                <span className="text-xs font-medium text-ink truncate">{r.list}</span>
                {r.reason && (
                  <span className="text-[10px] text-slate truncate hidden sm:block">
                    — {r.reason.slice(0, 60)}
                  </span>
                )}
              </div>
              {r.listed && r.delistUrl && (
                <a
                  href={r.delistUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-coral hover:underline shrink-0"
                >
                  Delist <ExternalLink size={10} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Warmup Pool Card (receiver inboxes) ─────────────────────────────────────

const PROVIDER_LABELS: Record<string, string> = {
  gmail:   "Gmail",
  outlook: "Outlook",
  yahoo:   "Yahoo",
  icloud:  "iCloud",
  custom:  "Custom",
};

const PROVIDER_COLORS: Record<string, string> = {
  gmail:   "bg-red-50 text-red-700",
  outlook: "bg-blue-50 text-blue-700",
  yahoo:   "bg-purple-50 text-purple-700",
  icloud:  "bg-slate-100 text-slate-700",
  custom:  "bg-amber-50 text-amber-700",
};

function AddInboxModal({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const [provider, setProvider] = useState<WarmupInboxInput["provider"]>("gmail");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("");

  // Custom-only fields
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [imapTls, setImapTls]   = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!email) {
      setError("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      const body: WarmupInboxInput = {
        email: email.trim().toLowerCase(),
        provider,
        label: label.trim() || null,
      };
      // Only include IMAP fields if the user entered a password.
      // Without a password, we'll just send (no unspam step).
      if (password.trim()) {
        body.imap_password = password;
        if (provider === "custom") {
          body.imap_host = imapHost.trim();
          body.imap_port = imapPort;
          body.imap_tls = imapTls;
        }
      }
      await deliverability.addInbox(body);
      onAdded();
      onClose();
    } catch (e: any) {
      setError(e.message || "Failed to add inbox");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-black/[0.06] shrink-0">
          <p className="font-semibold text-ink">Add warmup inbox</p>
          <button onClick={onClose} className="text-slate hover:text-ink">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="p-3 rounded-xl bg-teal/5 border border-teal/20 text-xs text-slate">
            <p className="font-medium text-ink mb-0.5">How this works</p>
            <p className="leading-relaxed">
              Your campaign SMTP will send small emails to this inbox each day. If you add an
              IMAP password, we'll also move those messages out of spam automatically — that's
              what trains your sender reputation. Without IMAP, we just send.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate block mb-1.5">Provider</label>
            <div className="grid grid-cols-5 gap-1.5">
              {(["gmail", "outlook", "yahoo", "icloud", "custom"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setProvider(p)}
                  className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    provider === p
                      ? "bg-teal text-white border-teal"
                      : "border-black/[0.08] text-slate hover:border-black/20"
                  }`}
                >
                  {PROVIDER_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate block mb-1.5">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-teal"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate block mb-1.5">
              {provider === "gmail" || provider === "yahoo" || provider === "icloud" ? "App password" : "IMAP password"}
              <span className="ml-1.5 text-slate/60 font-normal">(optional)</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                provider === "gmail"  ? "16-char Google app password"  :
                provider === "yahoo"  ? "Yahoo app password"           :
                provider === "icloud" ? "Apple app-specific password"  :
                "IMAP password"
              }
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm focus:outline-none focus:border-teal"
            />
            <p className="text-[11px] text-slate mt-1">
              {password ? (
                provider === "gmail"  ? "Generate at myaccount.google.com → Security → App passwords (2FA must be on)" :
                provider === "yahoo"  ? "Generate at login.yahoo.com → Account Security → App password" :
                provider === "icloud" ? "Generate at appleid.apple.com → Sign-In and Security → App-Specific Passwords" :
                "IMAP login credentials — we'll move warmup mail out of spam for you."
              ) : (
                <>Leave blank to skip IMAP. We'll still send to this inbox, but we won't be able to move mail out of spam automatically — you'll need to do that manually for best results.</>
              )}
            </p>
          </div>

          {provider === "custom" && (
            <div className="space-y-3 p-3 rounded-xl bg-black/[0.02] border border-black/[0.06]">
              <p className="text-xs font-semibold text-ink">Custom IMAP</p>
              <div className="grid grid-cols-3 gap-2">
                <input
                  className="col-span-2 px-3 py-2 rounded-lg border border-black/[0.08] text-sm"
                  placeholder="imap.example.com"
                  value={imapHost}
                  onChange={(e) => setImapHost(e.target.value)}
                />
                <input
                  type="number"
                  className="px-3 py-2 rounded-lg border border-black/[0.08] text-sm"
                  placeholder="993"
                  value={imapPort}
                  onChange={(e) => setImapPort(parseInt(e.target.value) || 993)}
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate">
                <input type="checkbox" checked={imapTls} onChange={(e) => setImapTls(e.target.checked)} />
                Use TLS
              </label>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate block mb-1.5">Label (optional)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Personal Gmail"
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] text-sm"
            />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-2 p-5 border-t border-black/[0.06] shrink-0">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-full text-sm font-medium border border-black/[0.08] text-slate hover:border-black/20 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-full text-sm font-semibold bg-teal text-white hover:bg-teal/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Testing & saving…" : "Add inbox"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InboxRow({
  inbox,
  onChange,
}: {
  inbox: WarmupInbox;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const toggleActive = async () => {
    setBusy("active");
    try { await deliverability.updateInbox(inbox.id, { is_active: !inbox.is_active }); onChange(); }
    finally { setBusy(null); }
  };
  const test = async () => {
    setBusy("test");
    try { await deliverability.testInbox(inbox.id); onChange(); }
    finally { setBusy(null); }
  };
  const remove = async () => {
    if (!confirm(`Remove ${inbox.email} from the warmup pool?`)) return;
    setBusy("delete");
    try { await deliverability.deleteInbox(inbox.id); onChange(); }
    finally { setBusy(null); }
  };

  const statusOk = inbox.last_test_status === "ok";
  const noImap = inbox.last_test_status === "no_imap";
  const progressPct = inbox.target_today > 0
    ? Math.min(100, (inbox.sent_today / inbox.target_today) * 100)
    : 0;

  return (
    <div className={`p-4 rounded-xl border transition-colors ${
      inbox.is_active ? "border-black/[0.08] bg-white" : "border-black/[0.06] bg-black/[0.02] opacity-70"
    }`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${PROVIDER_COLORS[inbox.provider]}`}>
            <Mail size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink truncate">{inbox.email}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[11px] text-slate">{PROVIDER_LABELS[inbox.provider]}</span>
              {inbox.label && <span className="text-[11px] text-slate">· {inbox.label}</span>}
              {statusOk ? (
                <span className="text-[11px] text-green-600 flex items-center gap-1">
                  <CheckCircle size={10} /> Connected
                </span>
              ) : noImap ? (
                <span className="text-[11px] text-slate flex items-center gap-1" title="No IMAP password — we'll send but won't move mail out of spam">
                  Send-only
                </span>
              ) : inbox.last_test_status ? (
                <span className="text-[11px] text-red-600 flex items-center gap-1" title={inbox.last_test_error || ""}>
                  <AlertTriangle size={10} /> {inbox.last_test_status}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={test}
            disabled={!!busy}
            title="Re-test connection"
            className="p-1.5 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04] disabled:opacity-50"
          >
            <RefreshCw size={13} className={busy === "test" ? "animate-spin" : ""} />
          </button>
          <button
            onClick={toggleActive}
            disabled={!!busy}
            title={inbox.is_active ? "Pause warmup" : "Resume warmup"}
            className={`p-1.5 rounded-lg hover:bg-black/[0.04] disabled:opacity-50 ${
              inbox.is_active ? "text-green-600" : "text-slate"
            }`}
          >
            <Power size={13} />
          </button>
          <button
            onClick={remove}
            disabled={!!busy}
            title="Remove inbox"
            className="p-1.5 rounded-lg text-slate hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {inbox.is_active && (
        <>
          {inbox.consecutive_send_failures > 0 && inbox.last_send_error && (
            <div className="mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <span className="font-semibold">Send failing</span> ({inbox.consecutive_send_failures}× in a row): {inbox.last_send_error}
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] text-slate mb-1">
            <span>Received today (campaign SMTP → this inbox)</span>
            <span className="font-semibold text-ink">{inbox.sent_today} / {inbox.target_today}</span>
          </div>
          <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
            <div
              className="h-full bg-teal transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2.5 text-[11px] text-slate">
            <span>Total received: {inbox.sent_total.toLocaleString()}</span>
            <span>Marked safe: {inbox.marked_safe_total.toLocaleString()}</span>
          </div>
        </>
      )}

      {!inbox.is_active && inbox.consecutive_send_failures > 0 && inbox.last_send_error && (
        <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700">
          <span className="font-semibold">Auto-paused</span> after {inbox.consecutive_send_failures} send failures. Last error: {inbox.last_send_error}.
          Re-enable to retry.
        </div>
      )}
    </div>
  );
}

function WarmupPoolCard() {
  const [stats, setStats]     = useState<WarmupPoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [resetting, setResetting]       = useState(false);
  const [testSending, setTestSending]   = useState(false);
  const [testResult, setTestResult]     = useState<{ sent: number; failed: number } | null>(null);

  const load = async () => {
    try {
      const res = await deliverability.getWarmupPool();
      setStats(res.stats);
    } catch (e: any) {
      setError(e.message || "Failed to load warmup pool");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleReset = async () => {
    if (!confirm("Reset all inbox ramps back to day 1?")) return;
    setResetting(true);
    try { await deliverability.resetWarmupPool(); await load(); }
    catch (e: any) { setError(e.message || "Failed to reset"); }
    finally { setResetting(false); }
  };

  const handleTestSend = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await deliverability.testSend();
      setTestResult({ sent: res.sent, failed: res.failed });
      setTimeout(() => setTestResult(null), 5000);
    } catch (e: any) {
      setError(e.message || "Test send failed");
    } finally {
      setTestSending(false);
    }
  };

  if (loading) return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-8 flex items-center justify-center">
      <Spinner />
    </div>
  );

  const inboxes = stats?.inboxes || [];
  const activeCount = inboxes.filter((i) => i.is_active).length;
  const hasSmtp = stats?.hasCampaignSmtp ?? false;

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-teal" />
          <div>
            <p className="font-semibold text-ink">Warmup Pool</p>
            <p className="text-xs text-slate mt-0.5">
              {inboxes.length === 0
                ? "Add Gmail / Outlook / Yahoo / iCloud inboxes to receive warmup mail"
                : `${activeCount} of ${inboxes.length} inbox${inboxes.length !== 1 ? "es" : ""} receiving warmup`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {inboxes.length > 0 && (
            <button
              onClick={handleReset}
              disabled={resetting}
              title="Reset all ramps to day 1"
              className="p-2 rounded-full text-slate hover:text-amber-600 hover:bg-amber-50 disabled:opacity-50"
            >
              <RefreshCw size={14} className={resetting ? "animate-spin" : ""} />
            </button>
          )}
          {inboxes.length > 0 && (
            <button
              onClick={handleTestSend}
              disabled={testSending}
              title="Send a test email to all active warmup inboxes"
              className="px-4 py-2 rounded-full text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 shadow-sm flex items-center gap-1.5"
            >
              {testSending
                ? <><Loader2 size={13} className="animate-spin" /> Sending…</>
                : <><SendHorizonal size={13} /> Test send</>}
            </button>
          )}
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 rounded-full text-xs font-semibold bg-teal text-white hover:bg-teal/90 shadow-sm flex items-center gap-1.5"
          >
            <Plus size={13} /> Add inbox
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {testResult && (
        <div className={`mb-4 p-3 rounded-xl border text-xs font-medium flex items-center gap-2 ${
          testResult.failed === 0
            ? "bg-green-50 border-green-200 text-green-700"
            : "bg-amber-50 border-amber-200 text-amber-800"
        }`}>
          <CheckCircle size={13} />
          {testResult.failed === 0
            ? `Test sent successfully to ${testResult.sent} inbox${testResult.sent !== 1 ? "es" : ""}.`
            : `Sent to ${testResult.sent}, failed for ${testResult.failed}.`}
        </div>
      )}

      {!hasSmtp && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <p className="font-semibold mb-0.5">Campaign SMTP not configured</p>
          <p>
            Warmup needs a sending SMTP. Configure your campaign SMTP in{" "}
            <a href="/settings" className="underline font-medium">Settings → Email Sending</a>{" "}
            first — the warmup engine will use it to send to your inboxes.
          </p>
        </div>
      )}

      {inboxes.length === 0 && (
        <div className="p-4 rounded-xl bg-black/[0.02] border border-black/[0.06] text-sm text-slate mb-4">
          <p className="font-medium text-ink mb-1">How it works</p>
          <p className="text-xs leading-relaxed">
            Add inboxes you own (personal Gmail, an Outlook account, Yahoo, etc.). Your campaign
            SMTP will send small AI-written emails to each one throughout the day, ramping from
            2 to 20 sends per inbox per day over five weeks. Each delivery is automatically moved
            out of spam — that's the unspam signal that trains your sender reputation.
          </p>
        </div>
      )}

      {inboxes.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: "Sent today",  value: `${stats?.sentToday ?? 0} / ${stats?.dailyTarget ?? 0}` },
            { label: "Sent total",  value: (stats?.sentTotal ?? 0).toLocaleString()                },
            { label: "Marked safe", value: (stats?.markedSafe ?? 0).toLocaleString()               },
          ].map(({ label, value }) => (
            <div key={label} className="bg-black/[0.02] rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-ink">{value}</p>
              <p className="text-[11px] text-slate mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {inboxes.length > 0 && (
        <div className="space-y-2.5">
          {inboxes.map((inbox) => (
            <InboxRow key={inbox.id} inbox={inbox} onChange={load} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddInboxModal
          onClose={() => setShowAdd(false)}
          onAdded={load}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DeliverabilityPage() {
  const [data, setData] = useState<DeliverabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Warmup modal state
  const [showWarmupModal, setShowWarmupModal] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [resetting, setResetting] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await deliverability.stats();
      setData(res);
    } catch (e: any) {
      setError(e.message || "Failed to load deliverability data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreateWarmup = async () => {
    // Strip protocol, paths, query strings, trailing slash — accept only the hostname
    let d = newDomain.trim().toLowerCase()
      .replace(/^https?:\/\//, "")   // remove protocol
      .split("/")[0]                  // remove any path
      .split("?")[0]                  // remove query string
      .split("#")[0]                  // remove fragment
      .replace(/\.$/, "");            // remove trailing dot
    if (!d || d.length < 3) {
      setDomainError("Enter a valid domain (e.g. mail.yourcompany.com)");
      return;
    }
    if (!/^[a-zA-Z0-9.-]+$/.test(d)) {
      setDomainError("Domain must contain only letters, numbers, hyphens, and dots");
      return;
    }
    setDomainError(null);
    setCreating(true);
    try {
      await deliverability.createWarmup(d);
      setShowWarmupModal(false);
      setNewDomain("");
      load();
    } catch (e: any) {
      setDomainError(e.message || "Failed to create warmup plan");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteWarmup = async (id: string) => {
    if (!confirm("Stop this warmup plan?")) return;
    setDeleting(id);
    try {
      await deliverability.deleteWarmup(id);
      load();
    } catch (e: any) {
      alert(e.message || "Failed to stop warmup");
    } finally {
      setDeleting(null);
    }
  };

  const handleResetWarmup = async (id: string) => {
    if (!confirm("Reset this warmup plan back to day 1? Use this when you've changed SMTP accounts.")) return;
    setResetting(id);
    try {
      await deliverability.resetWarmup(id);
      load();
    } catch (e: any) {
      alert(e.message || "Failed to reset warmup");
    } finally {
      setResetting(null);
    }
  };

  if (loading) return <Spinner className="h-screen" />;

  const t = data?.totals;
  const rep = (t && t.reputationScore !== null) ? reputationColor(t.reputationScore) : null;

  // Format trend data for chart
  const chartData = (data?.trend ?? []).map((w) => ({
    week: new Date(w.week + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    "Open Rate %": w.openRate,
    "Bounce Rate %": w.bounceRate,
    "Complaint Rate %": w.complaintRate,
  }));

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Deliverability</h1>
          <p className="text-sm text-slate">Monitor sender reputation and manage domain warming</p>
        </div>
        <button
          onClick={() => setShowWarmupModal(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/20">
          <Plus size={16} /> New Warmup Plan
        </button>
      </div>

      <div className="p-8 space-y-8">
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── Reputation score ── */}
        {t && rep && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className={rep.text} />
                <h2 className="font-bold text-ink">Sender Reputation</h2>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${rep.text} bg-opacity-10 ${
                t.reputationScore >= 80 ? "bg-green-100" :
                t.reputationScore >= 60 ? "bg-amber-100" : "bg-red-100"
              }`}>
                {rep.label}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-5xl font-black ${rep.text}`}>{t.reputationScore}</span>
              <div className="flex-1">
                <div className="h-3 bg-black/[0.05] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${rep.bg} rounded-full transition-all`}
                    style={{ width: `${t.reputationScore}%` }}
                  />
                </div>
                <p className="text-xs text-slate mt-2">
                  Based on your bounce rate ({t.bounceRate}%), complaint rate ({t.complaintRate}%), and open rate ({t.openRate}%).
                  {t.bounceRate > 2 && (
                    <span className="text-red-500 font-semibold ml-1">
                      ⚠ Bounce rate above 2% — check your list quality.
                    </span>
                  )}
                  {t.complaintRate > 0.1 && (
                    <span className="text-red-500 font-semibold ml-1">
                      ⚠ Complaint rate above 0.1% — this can hurt your warmup ramp.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Metric cards ── */}
        {t && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              label="Open Rate" value={`${t.openRate}%`}
              sub="Industry avg: 21.5%" icon={TrendingUp}
              good="up" trend={t.openRate >= 20 ? "up" : "down"}
            />
            <MetricCard
              label="Bounce Rate" value={`${t.bounceRate}%`}
              sub="Stay below 2%" icon={AlertTriangle}
              good="down" trend={t.bounceRate < 2 ? "down" : "up"}
            />
            <MetricCard
              label="Complaint Rate" value={`${t.complaintRate}%`}
              sub="Stay below 0.1%" icon={MessageCircleWarning}
              good="down" trend={t.complaintRate < 0.1 ? "down" : "up"}
            />
            <MetricCard
              label="Total Sent" value={t.sent.toLocaleString()}
              sub="All time" icon={TrendingUp}
            />
            <MetricCard
              label="Total Bounces" value={t.bounces.toLocaleString()}
              sub="Hard + soft" icon={AlertTriangle}
            />
          </div>
        )}

        {/* ── Trend chart ── */}
        {chartData.length > 0 && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
            <h2 className="font-bold text-ink mb-4">12-Week Trend</h2>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <XAxis dataKey="week" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="%" />
                <Tooltip formatter={(v: number) => `${v}%`} />
                <Legend />
                <Line type="monotone" dataKey="Open Rate %" stroke="#0A8F7F" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Bounce Rate %" stroke="#E85D4A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Complaint Rate %" stroke="#E8A317" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {chartData.length === 0 && !loading && (
          <div className="bg-white rounded-2xl border border-black/[0.06] p-12 text-center text-slate">
            <TrendingUp size={32} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No trend data yet</p>
            <p className="text-sm mt-1">Send your first campaign to start seeing deliverability metrics.</p>
          </div>
        )}

        {/* ── Warmup plan ── */}
        <div>
          <h2 className="font-bold text-ink mb-3 flex items-center gap-2">
            <Flame size={16} className="text-coral" /> Domain Warming
          </h2>
          {data?.warmup ? (
            <WarmupCard
              plan={data.warmup}
              isDeleting={deleting === data.warmup.id}
              onDelete={() => handleDeleteWarmup(data.warmup!.id)}
              isResetting={resetting === data.warmup.id}
              onReset={() => handleResetWarmup(data.warmup!.id)}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-8 text-center">
              <Flame size={28} className="mx-auto mb-3 text-slate opacity-40" />
              <p className="font-semibold text-ink mb-1">No active warmup plan</p>
              <p className="text-sm text-slate mb-4">
                Start a warmup plan to gradually increase your sending volume and protect your sender reputation.
              </p>
              <button onClick={() => setShowWarmupModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors">
                <Plus size={15} /> Start Warming
              </button>
            </div>
          )}
        </div>

        {/* ── IP Health / Blacklist ── */}
        <div>
          <h2 className="font-bold text-ink mb-3 flex items-center gap-2">
            <ShieldCheck size={16} className="text-teal" /> IP Blacklist Status
          </h2>
          <IpHealthCard initial={data?.blacklist ?? null} />
        </div>

        {/* ── Warmup Pool ── */}
        <div>
          <h2 className="font-bold text-ink mb-3 flex items-center gap-2">
            <Users size={16} className="text-teal" /> Cross-User Warmup Pool
          </h2>
          <WarmupPoolCard />
        </div>
      </div>

      {/* ── Create warmup modal ── */}
      {showWarmupModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
            <button onClick={() => { setShowWarmupModal(false); setNewDomain(""); setDomainError(null); }}
              className="absolute top-4 right-4 text-slate hover:text-ink">
              <X size={20} />
            </button>
            <h2 className="text-lg font-bold mb-1">Start Domain Warming</h2>
            <p className="text-sm text-slate mb-5">
              Enter your sending domain. MoreMail will automatically limit daily sends to protect your reputation.
            </p>

            {/* Ramp preview table */}
            <div className="mb-5 bg-black/[0.02] rounded-xl p-4">
              <p className="text-xs font-bold text-slate uppercase tracking-wider mb-3">Ramp Schedule</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                {RAMP.filter((_, i) => i < 8).map(([day, limit]) => (
                  <div key={day} className="bg-white rounded-lg p-2 border border-black/[0.06]">
                    <p className="text-xs font-bold text-ink">{limit >= 1000 ? `${limit / 1000}k` : limit}</p>
                    <p className="text-[10px] text-slate mt-0.5">Day {day}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                Sending Domain
              </label>
              <input
                type="text"
                placeholder="mail.yourcompany.com"
                value={newDomain}
                onChange={(e) => { setNewDomain(e.target.value); setDomainError(null); }}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 bg-paper ${
                  domainError ? "border-red-400" : "border-black/[0.08]"
                }`}
              />
              {domainError && <p className="mt-1 text-xs text-red-500">{domainError}</p>}
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowWarmupModal(false); setNewDomain(""); setDomainError(null); }}
                className="px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.08] hover:border-black/20">
                Cancel
              </button>
              <button onClick={handleCreateWarmup} disabled={creating}
                className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
                {creating ? "Starting…" : "Start Warming"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
